import type { FC } from 'react';

import { CityGlyph } from 'src/tv/live/CityIcons';
import { groundCircle, project, toPath } from 'src/tv/live/MapFrame';
import type { Point } from 'src/tv/live/MapFrame';
import type { Call } from 'src/tv/live/places';
import { OFFICES } from 'src/tv/live/places';
import { TV_COLORS } from 'src/tv/tvTheme';

/** Every call in the brand blue: green and amber mean a goal reached and a warning on the TV screens. */
const CALL_COLOR = TV_COLORS.accent;
const ICON_SIZE = 4.2;

const TRAVEL_MS = 1800;
// The call stays on the map a while after landing, so a room glancing at the TV still sees it.
const HOLD_MS = 12_000;
const FADE_MS = 3000;
const CALL_LIFETIME_MS = TRAVEL_MS + HOLD_MS + FADE_MS;

const arcPoint = (call: Call, t: number): Point => {
  const { office, customer } = call;
  const distance = Math.hypot(customer.x - office.x, customer.y - office.y);
  const height = 4 + distance * 0.35;

  return project(
    office.x + (customer.x - office.x) * t,
    office.y + (customer.y - office.y) * t,
    Math.sin(Math.PI * t) * height
  );
};

const ARC_STEPS = 48;

const CallArc: FC<{ call: Call; now: number }> = (props) => {
  const { call, now } = props;
  const age = now - call.receivedAt;
  const progress = Math.min(1, age / TRAVEL_MS);
  const eased = 1 - (1 - progress) ** 3;
  const opacity = age < TRAVEL_MS + HOLD_MS ? 1 : Math.max(0, 1 - (age - TRAVEL_MS - HOLD_MS) / FADE_MS);
  const steps = Math.max(1, Math.round(ARC_STEPS * eased));
  const points = Array.from({ length: steps + 1 }, (_, i) => arcPoint(call, (i / steps) * eased));
  const [headX, headY] = points[points.length - 1];
  const sinceArrival = age - TRAVEL_MS;

  return (
    <g opacity={opacity}>
      <path
        d={toPath(points)}
        fill="none"
        stroke={CALL_COLOR}
        strokeWidth={1.4}
        opacity={0.35}
        filter="url(#tv-map-glow)"
      />
      <path
        d={toPath(points)}
        fill="none"
        stroke={CALL_COLOR}
        strokeWidth={0.5}
        strokeLinecap="round"
      />
      {progress < 1 && <circle
        cx={headX}
        cy={headY}
        r={1.1}
        fill="#fff"
        filter="url(#tv-map-glow)"
      />}
      {sinceArrival > 0 && (
        <>
          <path
            d={groundCircle(call.customer, 1 + (sinceArrival % 1400) / 1400 * 5)}
            fill="none"
            stroke={CALL_COLOR}
            strokeWidth={0.4}
            opacity={1 - (sinceArrival % 1400) / 1400}
          />
          <path
            d={groundCircle(call.customer, 0.9)}
            fill={CALL_COLOR}
          />
        </>
      )}
    </g>
  );
};

const OfficeBeacons: FC = () => (
  <>
    {Object.values(OFFICES).map((office) => {
      const [baseX, baseY] = project(office.x, office.y);
      const [topX, topY] = project(office.x, office.y, 6);
      const isLabelLeft = office.id === 'oslo';
      const labelY = topY - (office.id === 'uppsala' ? 1.6 : -1.6);

      return (
        <g key={office.id}>
          <path
            d={groundCircle(office, 1.6)}
            fill={CALL_COLOR}
            opacity={0.25}
          />
          <line
            x1={baseX}
            y1={baseY}
            x2={topX}
            y2={topY}
            stroke={CALL_COLOR}
            strokeWidth={0.5}
          />
          <circle
            cx={topX}
            cy={topY}
            r={1.3}
            fill={CALL_COLOR}
            filter="url(#tv-map-glow)"
          />
          <circle
            cx={topX}
            cy={topY}
            r={0.7}
            fill="#fff"
          />
          <svg
            x={isLabelLeft ? topX - 2 - ICON_SIZE : topX + 2}
            y={labelY - ICON_SIZE + 0.6}
            width={ICON_SIZE}
            height={ICON_SIZE}
            viewBox="0 0 24 24"
            color={TV_COLORS.text}
          >
            <CityGlyph office={office.id} />
          </svg>
          <text
            x={topX + (isLabelLeft ? -2.8 - ICON_SIZE : 2.8 + ICON_SIZE)}
            y={labelY}
            textAnchor={isLabelLeft ? 'end' : 'start'}
            fill={TV_COLORS.text}
            fontSize={3.2}
            fontWeight={700}
          >
            {office.name}
          </text>
        </g>
      );
    })}
  </>
);

/** Each call still on screen as an arc, and the offices it rises from. */
export const CallLayer: FC<{ calls: Call[]; now: number }> = (props) => {
  const { calls, now } = props;

  return (
    <>
      {calls.filter((call) => now - call.receivedAt < CALL_LIFETIME_MS).map((call) => <CallArc
        key={call.id}
        call={call}
        now={now}
      />)}
      <OfficeBeacons />
    </>
  );
};
